import { isPlatformBrowser } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { ApplicationFormService } from '../../../services/application-form.service';
import { CandidateCreateContext, CandidateService, CreatableCandidate } from '../../../services/candidate.service';
import { GlobalContentService } from '../../../services/global-content.service';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { ApplicationFormSuccessComponent } from './components/application-form-success/application-form-success.component';

@Component({
    selector: 'app-application-form',
    templateUrl: './application-form.component.html',
    styleUrls: ['./application-form.component.scss'],
    standalone: true,
    imports: [ProgressBarComponent, FormsModule, ReactiveFormsModule, RouterLink, ApplicationFormSuccessComponent]
})
export class ApplicationFormComponent implements OnInit {
    etracker?: Etracker;

    @Input() horizontalMode = false;
    @Input() modalMode = false;
    @Output() close: EventEmitter<void> = new EventEmitter();

    job?: string;
    organization?: string;

    hideDataPrivacyOption = false;
    DataPrivacyFieldId: string = Math.random().toString(36);

    Status = Status;
    status = Status.Default;
    errorMessageKey?: string;

    formGroup?: FormGroup;

    assessmentSessionUrl?: string;
    private platformId = inject<Object>(PLATFORM_ID);

    retryCount = 0;
    maxRetries = 3;

    constructor(
        private formBuilder: FormBuilder,
        private applicationFormService: ApplicationFormService,
        private candidateService: CandidateService,
        private globalContentService: GlobalContentService,
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.etracker = (window as { [key: string]: any })['_etracker'] as Etracker;
        }
    }

    ngOnInit(): void {
        const applicationFormData = this.applicationFormService.getApplicationFormData();
        this.job = applicationFormData.job;
        this.organization = applicationFormData.organization;

        this.setupForm();
        this.hideDataPrivacyOption = this.horizontalMode;
    }

    setupForm(): void {
        this.status = Status.Default;
        this.formGroup = this.formBuilder.group({
            name: new FormControl('', [Validators.required]),
            email: new FormControl('', [Validators.required, Validators.email])
        });
    }

    submit() {
        if (this.formGroup?.invalid) {
            for (var i in this.formGroup.controls) {
                this.formGroup.controls[i].markAsTouched();
            }
            return;
        }

        const candidate: CreatableCandidate = {
            name: this.formGroup?.controls['name'].value,
            email: this.formGroup?.controls['email'].value,
            job: this.job || environment.applicationConfig.jobId,
            organization: this.organization || environment.applicationConfig.organizationId,
            returnAssessmentSession: true,
            createAssessmentSession: true,
            preferredLanguage: 'de',
            context: this.getContext()
        };

        this.status = Status.Pending;
        this.candidateService.createCandidate(candidate).subscribe({
            next: (candidateID) => {
                this.etracker?.sendEvent(new et_ClickEvent('Beworben'));
                this.getAssessmentUrl(candidateID);
            },
            error: (error) => {
                this.status = Status.Error;
                switch (error.status) {
                    case 500: {
                        switch (error.error.code) {
                            case '40040AC':
                                this.status = Status.TalogyError;
                                this.errorMessageKey = `<p>Vielen Dank für deine Bewerbung. Wir schicken dir einen Link zu deinem persönlichen Assessment Center per E-Mail zu. <br> Viel Erfolg bei der Durchführung!</p>`;
                                break;
                            case '40040EE':
                                this.errorMessageKey = '<p>Sorry ☹️ Du hast dich bereits mit dieser E-Mail Adresse beworben.</p>';
                                break;
                            default:
                                this.errorMessageKey = '<p>Sorry ☹️ Ein Fehler ist aufgetreten. Bitte versuche es später erneut.</p>';
                                break;
                        }
                        break;
                    }
                    default: {
                        this.errorMessageKey = '<p>Sorry ☹️ Ein Fehler ist aufgetreten. Bitte versuche es später erneut.</p>';
                    }
                }
            }
        });
    }

    getContext(): CandidateCreateContext {
        return {
            firstVisitPage: localStorage.getItem('firstVisitPage') || '',
            firstVisitDate: localStorage.getItem('firstVisitDate') || ''
        };
    }

    closeModal(): void {
        this.close.emit();
    }


    getAssessmentUrl = (candidateID:string) => {
        this.candidateService.getAssessmentCenterURL(candidateID).subscribe({
            next: (assessmentSessionUrl) => {
                this.assessmentSessionUrl = assessmentSessionUrl;
                this.status = Status.Success;
            },
            error: (error) => {
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    console.log(`Fehler beim Abrufen der Assessment-URL. Versuch ${this.retryCount} von ${this.maxRetries}...`);
                    setTimeout(() => this.getAssessmentUrl(candidateID), 1000 * this.retryCount); // Exponentielles Backoff
                    return;
                }

                this.status = Status.Error;
                switch (error.status) {
                    case 500: {
                        switch (error.error.code) {
                            case '40040AC':
                                this.status = Status.TalogyError;
                                this.errorMessageKey = `<p>Vielen Dank für deine Bewerbung. Wir schicken dir einen Link zu deinem persönlichen Assessment Center per E-Mail zu. <br> Viel Erfolg bei der Durchführung!</p>`;
                                break;
                            case '40040EE':
                                this.errorMessageKey = '<p>Sorry ☹️ Du hast dich bereits mit dieser E-Mail Adresse beworben.</p>';
                                break;
                            default:
                                this.errorMessageKey = '<p>Sorry ☹️ Ein Fehler ist aufgetreten. Bitte versuche es später erneut.</p>';
                                break;
                        }
                        break;
                    }
                    default: {
                        this.errorMessageKey = '<p>Sorry ☹️ Ein Fehler ist aufgetreten. Bitte versuche es später erneut.</p>';
                    }
                }
            }
        });
    };
}

export enum Status {
    Default,
    Pending,
    Error,
    Success,
    TalogyError
}
